import { useEffect, useRef } from 'react';

// A scanner (barcode or RFID) is a HID keyboard-emulation device: it "types" the
// decoded value into whatever has focus, then sends Enter. A human never types
// fast enough to beat this gap, so a short debounce is what tells a scan apart
// from ordinary typing.
const SCAN_KEY_GAP_MS = 100;

// Captures a hardware scan (barcode or RFID reader) as a finished code string.
// `onScan` fires once per completed scan; what the code means is the caller's
// business logic, not this hook's - it only owns the keystroke buffering.
export const useScanCapture = (onScan, { enabled = true } = {}) => {
    const bufferRef = useRef('');
    const timeoutRef = useRef(null);
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    useEffect(() => {
        if (!enabled) return undefined;

        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

            if (e.key === 'Enter') {
                const code = bufferRef.current.trim();
                bufferRef.current = '';
                if (code) onScanRef.current(code);
                return;
            }

            if (e.key.length === 1) {
                bufferRef.current += e.key;
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    bufferRef.current = '';
                }, SCAN_KEY_GAP_MS);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            clearTimeout(timeoutRef.current);
            bufferRef.current = '';
        };
    }, [enabled]);
};
